/**
 * tailors-directory.js
 * Handles search filtering, city filtering, type filtering, and pagination
 * for the Tailors Directory section.
 */

(function () {
  'use strict';

  class TailorsDirectory {
    constructor(sectionId) {
      this.sectionId = sectionId;
      this.config = window.TailorDirectoryConfig?.[sectionId] || { stockistLabel: 'Stockist', allCitiesLabel: 'All Cities' };
      this.stockistLabel = this.config.stockistLabel;
      this.allCitiesLabel = this.config.allCitiesLabel || 'All Cities';

      // State
      this.allTailors = [];
      this.filteredTailors = [];
      this.searchQuery = '';
      this.activeType = 'all';
      this.activeCity = 'all';

      // Elements
      this.grid = document.getElementById(`tailors-grid-${sectionId}`);
      this.searchInput = document.getElementById(`tailors-search-${sectionId}`);
      this.searchClear = document.getElementById(`tailors-search-clear-${sectionId}`);
      this.countEl = document.getElementById(`tailors-count-${sectionId}`);
      this.cityTabsEl = document.getElementById(`tailors-city-tabs-${sectionId}`);
      this.emptyEl = document.getElementById(`tailors-empty-${sectionId}`);
      this.resetBtn = document.getElementById(`tailors-reset-${sectionId}`);
      this.typeBtns = document.querySelectorAll(`#tailors-directory-${sectionId} .tailors-directory__type-btn`);

      if (!this.grid) return;

      this._loadData();
      this._bindEvents();
    }

    // ─── Data ────────────────────────────────────────────────────────────────

    _loadData() {
      const dataEl = document.getElementById(`tailors-data-${this.sectionId}`);
      if (!dataEl) return;

      try {
        this.allTailors = JSON.parse(dataEl.textContent);
      } catch (e) {
        console.error('[TailorsDirectory] Failed to parse tailor data:', e);
        this.allTailors = [];
      }

      this.filteredTailors = [...this.allTailors];
      this._buildCityTabs();
      this._render();
    }

    // ─── City Tabs ───────────────────────────────────────────────────────────

    _buildCityTabs() {
      if (!this.cityTabsEl) return;

      // Count tailors per city, skipping blanks
      const cityCounts = {};
      this.allTailors.forEach((tailor) => {
        const city = (tailor.city || '').trim();
        if (!city) return;
        cityCounts[city] = (cityCounts[city] || 0) + 1;
      });

      const cities = Object.keys(cityCounts).sort((a, b) => a.localeCompare(b));

      // Nothing to group by — hide the tab bar entirely
      if (cities.length <= 1) {
        this.cityTabsEl.hidden = true;
        return;
      }

      this.cityTabsEl.hidden = false;

      const allTab = `
        <button
          class="tailors-directory__city-tab active"
          data-city="all"
          role="tab"
          aria-selected="true"
        >
          ${this._escHtml(this.allCitiesLabel)}
          <span class="tailors-directory__city-tab-count">${this.allTailors.length}</span>
        </button>`;

      const cityTabs = cities
        .map(
          (city) => `
        <button
          class="tailors-directory__city-tab"
          data-city="${this._escAttr(city)}"
          role="tab"
          aria-selected="false"
        >
          ${this._escHtml(city)}
          <span class="tailors-directory__city-tab-count">${cityCounts[city]}</span>
        </button>`
        )
        .join('');

      this.cityTabsEl.innerHTML = allTab + cityTabs;

      this.cityTabsEl.querySelectorAll('.tailors-directory__city-tab').forEach((btn) => {
        btn.addEventListener('click', () => {
          if (btn.dataset.city === this.activeCity) return;

          this.cityTabsEl.querySelectorAll('.tailors-directory__city-tab').forEach((b) => {
            b.classList.remove('active');
            b.setAttribute('aria-selected', 'false');
          });
          btn.classList.add('active');
          btn.setAttribute('aria-selected', 'true');
          btn.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });

          this.activeCity = btn.dataset.city;
          this._applyFilters();
        });
      });
    }

    _setActiveCityTab(city) {
      if (!this.cityTabsEl) return;
      this.cityTabsEl.querySelectorAll('.tailors-directory__city-tab').forEach((btn) => {
        const isActive = btn.dataset.city === city;
        btn.classList.toggle('active', isActive);
        btn.setAttribute('aria-selected', String(isActive));
      });
    }

    // ─── Events ──────────────────────────────────────────────────────────────

    _bindEvents() {
      // Search input — debounced
      let debounceTimer;
      this.searchInput?.addEventListener('input', (e) => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          this.searchQuery = e.target.value.trim().toLowerCase();
          this.searchClear.hidden = !this.searchQuery;
          this._applyFilters();
        }, 250);
      });

      // Clear search
      this.searchClear?.addEventListener('click', () => {
        this.searchInput.value = '';
        this.searchQuery = '';
        this.searchClear.hidden = true;
        this.searchInput.focus();
        this._applyFilters();
      });

      // Type filter buttons
      this.typeBtns.forEach((btn) => {
        btn.addEventListener('click', () => {
          this.typeBtns.forEach((b) => b.classList.remove('active'));
          btn.classList.add('active');
          this.activeType = btn.dataset.type;
          this._applyFilters();
        });
      });

      // Reset / clear from empty state
      this.resetBtn?.addEventListener('click', () => {
        this._reset();
      });
    }

    // ─── Filtering ───────────────────────────────────────────────────────────

    _applyFilters() {
      this.filteredTailors = this.allTailors.filter((tailor) => {
        const matchesSearch =
          !this.searchQuery ||
          tailor.name?.toLowerCase().includes(this.searchQuery) ||
          tailor.city?.toLowerCase().includes(this.searchQuery) ||
          tailor.locality?.toLowerCase().includes(this.searchQuery);

        const matchesType =
          this.activeType === 'all' || tailor.tailor_type === this.activeType;

        const matchesCity =
          this.activeCity === 'all' || tailor.city === this.activeCity;

        return matchesSearch && matchesType && matchesCity;
      });

      this._render();
    }

    _reset() {
      this.searchQuery = '';
      this.activeType = 'all';
      this.activeCity = 'all';

      if (this.searchInput) this.searchInput.value = '';
      if (this.searchClear) this.searchClear.hidden = true;

      this.typeBtns.forEach((btn) => {
        btn.classList.toggle('active', btn.dataset.type === 'all');
      });
      this._setActiveCityTab('all');

      this.filteredTailors = [...this.allTailors];
      this._render();
    }

    // ─── Rendering ───────────────────────────────────────────────────────────

    _render() {
      this._renderCount();
      this._renderGrid();
    }

    _renderCount() {
      const total = this.filteredTailors.length;
      if (this.countEl) {
        this.countEl.textContent =
          total === 0
            ? 'No tailors found'
            : total === 1
            ? '1 tailor found'
            : `${total} tailors found`;
      }
    }

    _isFilterActive() {
      return !!this.searchQuery || this.activeType !== 'all' || this.activeCity !== 'all';
    }

    _renderGrid() {
      const pageTailors = this.filteredTailors;

      // Empty state — only surface this when the person has actively searched
      // or filtered and gotten zero results. If there are simply no tailors to
      // begin with (e.g. data hasn't loaded), stay silent instead of showing
      // a "no results" message on a page nobody searched on.
      const isEmpty = pageTailors.length === 0;
      const showEmptyState = isEmpty && this._isFilterActive();

      this.emptyEl.hidden = !showEmptyState;
      this.grid.hidden = isEmpty;

      if (isEmpty) return;

      this.grid.innerHTML = pageTailors
        .map((tailor, index) => this._buildCard(tailor, index))
        .join('');

      // Stagger animate cards in
      requestAnimationFrame(() => {
        const cards = this.grid.querySelectorAll('.tailor-card');
        cards.forEach((card, i) => {
          card.style.animationDelay = `${i * 50}ms`;
          card.classList.add('tailor-card--visible');
        });
      });
    }

    _buildCard(tailor, index) {
      const stockistBadge = tailor.is_stockist
        ? `<span class="tailor-card__stockist-badge">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
            ${this._escHtml(this.stockistLabel)}
           </span>`
        : '';

      const typeClass = tailor.tailor_type
        ? `tailor-card__type--${tailor.tailor_type.toLowerCase().replace(/[^a-z0-9]/g, '-')}`
        : '';

      const mapBtn = tailor.map_link
        ? `<a
             href="${this._escAttr(tailor.map_link)}"
             class="tailor-card__map-btn"
             target="_blank"
             rel="noopener noreferrer"
             aria-label="Open ${this._escAttr(tailor.name)} on map"
           >
             <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
               <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
               <circle cx="12" cy="10" r="3"/>
             </svg>
             View on Map
           </a>`
        : '';

      const contactEl = tailor.contact
        ? `<a href="tel:${this._escAttr(tailor.contact)}" class="tailor-card__contact" aria-label="Call ${this._escAttr(tailor.name)}">
             <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
               <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.99 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.9 1.27h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 8.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
             </svg>
             ${this._escHtml(tailor.contact)}
           </a>`
        : '';

      return `
        <article class="tailor-card" role="listitem" aria-label="${this._escAttr(tailor.name)}">
          <div class="tailor-card__inner">
            <div class="tailor-card__top">
              <h3 class="tailor-card__name">${this._escHtml(tailor.name)}</h3>
              <p class="tailor-card__location">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
                ${tailor.locality ? `${this._escHtml(tailor.locality)}, ` : ''}${this._escHtml(tailor.city)}
              </p>
              <div class="tailor-card__meta">
                ${tailor.tailor_type ? `<span class="tailor-card__type ${typeClass}">${this._escHtml(tailor.tailor_type)}</span>` : ''}
                ${stockistBadge}
              </div>
            </div>
            <div class="tailor-card__bottom">
              ${contactEl}
              ${mapBtn}
            </div>
          </div>
        </article>
      `;
    }

    // ─── Helpers ─────────────────────────────────────────────────────────────

    _escHtml(str) {
      if (!str) return '';
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    _escAttr(str) {
      return this._escHtml(str);
    }
  }

  // ─── Init all sections on page ───────────────────────────────────────────

  function initAll() {
    const configs = window.TailorDirectoryConfig || {};
    Object.keys(configs).forEach((sectionId) => {
      new TailorsDirectory(sectionId);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Shopify Theme Editor live reload support
  document.addEventListener('shopify:section:load', (e) => {
    const sectionId = e.detail.sectionId;
    if (window.TailorDirectoryConfig?.[sectionId]) {
      new TailorsDirectory(sectionId);
    }
  });
})();